import commaNumber from 'comma-number';
import { ChevronRight } from 'lucide-react';
import { Fragment } from 'react';

import { VFlex } from '@/components/base';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useProductStore from '@/features/Product/store/ProductStore';
import type { ProductInfo } from '@/features/Product/types';
import { filterMap } from '@/utils/lazy';
import { isEmptyArray } from '@/utils/util';

import { ValueWrapper } from '../ValueWrapper';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';

type GroupedProduct = {
  currency?: string;
  price?: number;
  brandName?: string;
  brandId?: string;
  lineName?: string;
  lineId?: string;
  productName?: string;
  productId?: string;
  productOptionArray: {
    id?: string;
    name?: string;
    currency?: string;
    price?: number;
  }[];
};

export const ProductField = (
  props: CommonFieldProps & {
    products: ProductInfo[];
  },
) => {
  const { brandMap, lineMap, groupedProductMap } = useProductStore();
  const getProductKey = (product: ProductInfo) => {
    return `${product.brand_id}-${product.line_id}-${product.product_id}`;
  };
  const groupedProducts = props.products.reduce((acc, product) => {
    if (!groupedProductMap.get(product.alias)) return acc;
    if (!product.product_id) return acc;
    const productKey = getProductKey(product);
    const brandName = brandMap.get(product.brand_id || '')?.name;
    const lineName = lineMap.get(product.line_id || '')?.name;
    const productObj = groupedProductMap.get(product.alias || '');
    const prevOptionArray = acc.get(productKey)?.productOptionArray || [];
    const newOptionArray = [...prevOptionArray];
    if (product.product_option_id) {
      newOptionArray.push({
        id: product.product_option_id,
        name: productObj?.options?.find(
          (option) => option.id === product.product_option_id,
        )?.option_name,
        currency: product.currency,
        price: product.price,
      });
    }
    acc.set(productKey, {
      brandName: brandName,
      currency: product.currency,
      price: product.price,
      lineName: lineName,
      productName: productObj?.product_name,
      brandId: product.brand_id,
      lineId: product.line_id,
      productId: product.product_id,
      productOptionArray: newOptionArray,
    });
    return acc;
  }, new Map<string, GroupedProduct>());

  if (!props.showFull && isEmptyArray(props.products)) return null;

  return (
    <PropertyRowWrapper {...props} expandable>
      <VFlex g={2}>
        {groupedProducts.size > 0 ? (
          Array.from(groupedProducts.entries()).map(
            ([productKey, groupedProduct]) => (
              <ProductInfoItem
                key={productKey}
                showFull={props.showFull}
                {...groupedProduct}
              />
            ),
          )
        ) : (
          <PropertyValue placeholder={NOT_ASSIGNED_LABEL} />
        )}
      </VFlex>
    </PropertyRowWrapper>
  );
};

const ProductInfoItem = (props: GroupedProduct & { showFull: boolean }) => {
  const isPriceSame = props.productOptionArray.every(
    (option) => option.price === props.price,
  );

  if (!props.showFull) {
    return (
      <TooltipV2 tooltipNode={'Product'}>
        <div>
          <PropertyValue isEllipsis={true} value={props.productName} />
        </div>
      </TooltipV2>
    );
  }

  return (
    <VFlex g={4}>
      <div className="flex items-center flex-wrap">
        {filterMap(
          [
            {
              name: props.brandName,
              id: props.brandId,
              type: 'Brand',
            },
            {
              name: props.lineName,
              id: props.lineId,
              type: 'Product line',
            },
            {
              name: props.productName,
              id: props.productId,
              type: 'Product',
            },
          ],
          (item) => {
            if (!item.name) return null;
            return item;
          },
        ).map((item, index, array) => (
          <Fragment key={item.id}>
            <TooltipV2 tooltipNode={item.type}>
              <div>
                <PropertyValue value={item.name} />
              </div>
            </TooltipV2>
            {index !== array.length - 1 && (
              <ChevronRight size={14} className="text-gray-600 -mx-1" />
            )}
          </Fragment>
        ))}
        {props.productOptionArray.length === 0 &&
          props.price &&
          props.currency && (
            <ValueWrapper>
              <Typo type="label3" className="text-gray-600">
                {commaNumber(props.price)}&nbsp;
                {props.currency && props.currency.toUpperCase()}
              </Typo>
            </ValueWrapper>
          )}
      </div>
      <div className="flex flex-wrap gap-1">
        {props.productOptionArray.map((option, i) => {
          return (
            <Fragment key={option.id}>
              <TooltipV2 tooltipNode="Product option">
                <ValueWrapper className="gap-2 border border-gray-300 py-0.5">
                  <Typo type="label3" className="text-gray-600">
                    {option.name || NOT_ASSIGNED_LABEL}
                  </Typo>
                  {!isPriceSame && typeof option.price === 'number' && (
                    <Typo type="label3" className="text-gray-600">
                      {commaNumber(option.price)}&nbsp;
                      {option.currency && option.currency.toUpperCase()}
                    </Typo>
                  )}
                </ValueWrapper>
              </TooltipV2>
            </Fragment>
          );
        })}
      </div>
      {isPriceSame && typeof props.price === 'number' && (
        <ValueWrapper>
          <Typo type="label3" className="text-gray-600">
            {commaNumber(props.price)}&nbsp;
            {props.currency && props.currency.toUpperCase()}
          </Typo>
        </ValueWrapper>
      )}
    </VFlex>
  );
};
